import { supabase } from '@/lib/supabase'
import type {
  Project,
  ProjectFilters,
  ProjectFormData,
  ProjectTask,
  TaskFormData,
  ProjectDocument,
  ProjectMilestone,
  MilestoneFormData,
  DocumentCategory,
} from '@/types/project'

// Helper function to map database project to CRM project structure
const mapDbProjectToProject = (dbProject: any): Project => {
  return {
    id: dbProject.id,
    created_at: dbProject.created_at,
    updated_at: dbProject.updated_at,
    nom: dbProject.title || dbProject.nom || '',
    description: dbProject.description,
    status: dbProject.status || 'pending',
    client_id: dbProject.user_id || dbProject.client_id,
    admin_id: null, // Not available in current structure
    budget: dbProject.budget ? parseFloat(dbProject.budget) : null,
    budget_spent: null,
    date_debut: dbProject.created_at,
    date_fin: dbProject.deadline || dbProject.date_fin,
    progression: dbProject.progress || 0,
    client: dbProject.user ? {
      id: dbProject.user.id,
      full_name: dbProject.user.full_name || dbProject.contact_name || '',
      email: dbProject.user.email || dbProject.contact_email || '',
      company: dbProject.user.company || dbProject.company,
      avatar_url: dbProject.user.avatar_url,
    } : undefined,
    admin: undefined,
    tasks: [],
    documents: [],
    invoices: [],
    quotes: [],
    milestones: [],
    activities: [],
    // Include all metadata fields from database
    ...((dbProject.goals || dbProject.features || dbProject.target || dbProject.site_type ||
        dbProject.support_needed !== undefined || dbProject.accepted !== undefined) && {
      goals: dbProject.goals,
      features: dbProject.features,
      target: dbProject.target,
      site_type: dbProject.site_type,
      support_needed: dbProject.support_needed,
      support_contact: dbProject.support_contact,
      support_email: dbProject.support_email,
      thumbnail_url: dbProject.thumbnail_url,
      accepted: dbProject.accepted,
      rejection_reason: dbProject.rejection_reason,
      company: dbProject.company,
      contact_name: dbProject.contact_name,
      contact_email: dbProject.contact_email,
    }),
    // Include metadata JSONB (contains BMAD markdown for admin view)
    metadata: dbProject.metadata || {},
  } as any
}

export const projectsService = {
  // Projects CRUD
  async getAll(filters?: ProjectFilters, page = 1, limit = 20): Promise<{ projects: Project[]; total: number }> {
    const sortBy = filters?.sortBy ?? 'created_at'
    const sortAsc = filters?.sortOrder === 'asc'

    let query = supabase
      .from('projects')
      .select('*', { count: 'exact' })
      .order(sortBy, { ascending: sortAsc, nullsFirst: false })

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,company.ilike.%${filters.search}%`)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.clientId) {
      query = query.eq('user_id', filters.clientId)
    }

    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('deadline', filters.dateTo)
    }

    // Pagination
    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error

    // Fetch user details separately (workaround for PostgREST cache issue)
    const userIds = [...new Set((data || []).map(p => p.user_id).filter(Boolean))]
    let usersMap: Record<string, any> = {}

    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, full_name, email, company, avatar_url')
        .in('id', userIds)

      if (users) {
        usersMap = Object.fromEntries(users.map(u => [u.id, u]))
      }
    }

    // Map projects with user data
    const projects = (data || []).map(dbProject => ({
      ...mapDbProjectToProject(dbProject),
      client: dbProject.user_id && usersMap[dbProject.user_id] ? {
        id: usersMap[dbProject.user_id].id,
        full_name: usersMap[dbProject.user_id].full_name || dbProject.contact_name || '',
        email: usersMap[dbProject.user_id].email || dbProject.contact_email || '',
        company: usersMap[dbProject.user_id].company || dbProject.company,
        avatar_url: usersMap[dbProject.user_id].avatar_url,
      } : undefined,
    }))

    return {
      projects,
      total: count || 0,
    }
  },

  async getById(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error

    if (!data) return null

    // Fetch all related data in parallel for performance
    const userId = data.user_id

    const [userResult, docsResult, devisResult, factureResult] = await Promise.all([
      // User details
      userId
        ? supabase
            .from('users')
            .select('id, full_name, email, company, avatar_url')
            .eq('id', userId)
            .single()
        : Promise.resolve({ data: null, error: null }),

      // Documents from project_documents table
      supabase
        .from('project_documents')
        .select('*')
        .eq('project_id', id),

      // Devis (quotes)
      (() => {
        let q = supabase
          .from('devis')
          .select('id, numero, montant_ht, montant_ttc, status, date_emission, date_validite')
          .order('created_at', { ascending: false })
        if (userId) {
          q = q.or(`projet_id.eq.${id},contact_id.eq.${userId}`)
        } else {
          q = q.eq('projet_id', id)
        }
        return q
      })(),

      // Factures (invoices)
      (() => {
        let q = supabase
          .from('facture')
          .select('id, numero, total_ht, total_ttc, statut, date_creation, date_echeance')
          .order('created_at', { ascending: false })
        if (userId) {
          q = q.or(`projet_id.eq.${id},utilisateur_id.eq.${userId}`)
        } else {
          q = q.eq('projet_id', id)
        }
        return q
      })(),
    ])

    const user = userResult.data

    // Map the database project to CRM project
    const project = {
      ...mapDbProjectToProject(data),
      client: user ? {
        id: user.id,
        full_name: user.full_name || data.contact_name || '',
        email: user.email || data.contact_email || '',
        company: user.company || data.company,
        avatar_url: user.avatar_url,
      } : undefined,
    }

    // Documents
    if (!docsResult.error && docsResult.data) {
      project.documents = docsResult.data as any[]
    }

    project.tasks = []

    // Quotes
    if (!devisResult.error && devisResult.data) {
      const statusMap: Record<string, string> = {
        draft: 'brouillon',
        sent: 'envoye',
        viewed: 'envoye',
        converted: 'accepte',
        accepted: 'accepte',
        rejected: 'refuse',
        expired: 'expire',
      }
      project.quotes = devisResult.data.map((d: any) => ({
        id: d.id,
        numero: d.numero,
        total_ht: parseFloat(d.montant_ht) || 0,
        total_ttc: parseFloat(d.montant_ttc) || 0,
        status: statusMap[d.status] || d.status || 'brouillon',
        date_emission: d.date_emission,
        date_validite: d.date_validite,
      }))
    }

    // Invoices
    if (!factureResult.error && factureResult.data) {
      const statutMap: Record<string, string> = {
        draft: 'brouillon',
        brouillon: 'brouillon',
        envoyee: 'envoyee',
        en_attente: 'en_attente',
        payee: 'payee',
        cancelled: 'annulee',
        annulee: 'annulee',
      }
      project.invoices = factureResult.data.map((f: any) => ({
        id: f.id,
        numero: f.numero,
        total_ht: parseFloat(f.total_ht) || 0,
        total_ttc: parseFloat(f.total_ttc) || 0,
        statut: statutMap[f.statut] || f.statut || 'brouillon',
        date_emission: f.date_creation,
        date_echeance: f.date_echeance,
      }))
    }

    return project
  },

  async create(project: ProjectFormData): Promise<Project> {
    // Map CRM project data to database structure
    const dbProject = {
      title: project.nom,
      description: project.description || null,
      status: project.status,
      user_id: project.client_id, // Map client_id to user_id
      budget: project.budget?.toString() || null,
      deadline: project.date_fin || null,
      progress: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('projects')
      .insert(dbProject)
      .select(`
        *,
        user:users!projects_user_id_fkey(id, full_name, email, company, avatar_url)
      `)
      .single()

    if (error) throw error

    return mapDbProjectToProject(data)
  },

  async update(id: string, project: Partial<ProjectFormData>): Promise<Project> {
    // Map CRM project data to database structure
    const dbUpdate: any = {
      updated_at: new Date().toISOString(),
    }

    if (project.nom !== undefined) dbUpdate.title = project.nom
    if (project.description !== undefined) dbUpdate.description = project.description
    if (project.status !== undefined) dbUpdate.status = project.status
    if (project.client_id !== undefined) dbUpdate.user_id = project.client_id
    if (project.budget !== undefined) dbUpdate.budget = project.budget?.toString()
    if (project.date_fin !== undefined) dbUpdate.deadline = project.date_fin

    const { data, error } = await supabase
      .from('projects')
      .update(dbUpdate)
      .eq('id', id)
      .select(`
        *,
        user:users!projects_user_id_fkey(id, full_name, email, company, avatar_url)
      `)
      .single()

    if (error) throw error

    return mapDbProjectToProject(data)
  },

  async updateStatus(id: string, status: Project['status']): Promise<Project> {
    return this.update(id, { status })
  },

  async updateProgression(id: string, progression: number): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update({
        progress: Math.min(100, Math.max(0, progression)),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select(`
        *,
        user:users!projects_user_id_fkey(id, full_name, email, company, avatar_url)
      `)
      .single()

    if (error) throw error

    return mapDbProjectToProject(data)
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('projects').delete().eq('id', id)
    if (error) throw error
  },

  // ==================== TASKS ====================

  async getTasks(projectId: string): Promise<ProjectTask[]> {
    // Note: La table project_tasks n'existe pas encore dans la BDD
    // Utilisation de project_phases comme fallback temporaire
    try {
      const { data, error } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true })

      if (error) {
        console.warn('Error fetching project tasks/phases:', error)
        return []
      }

      // Map project_phases to ProjectTask format
      return (data || []).map(phase => ({
        id: phase.id,
        created_at: phase.created_at,
        updated_at: phase.updated_at,
        project_id: phase.project_id,
        titre: phase.name,
        description: phase.description,
        status: phase.status === 'completed' ? 'done' : phase.status === 'in_progress' ? 'in_progress' : 'todo',
        priority: 'medium' as const,
        due_date: phase.end_date,
        assigned_to: null,
        ordre: phase.order_index || 0,
        start_date: phase.start_date || null,
        end_date: phase.end_date || null,
        notes: phase.notes || null,
        progress: phase.progress || 0,
      })) as ProjectTask[]
    } catch {
      console.warn('Table project_tasks/project_phases not available')
      return []
    }
  },

  async createTask(projectId: string, task: TaskFormData): Promise<ProjectTask> {
    // Utilisation de project_phases comme fallback
    const { data: maxOrderData } = await supabase
      .from('project_phases')
      .select('order_index')
      .eq('project_id', projectId)
      .order('order_index', { ascending: false })
      .limit(1)
      .single()

    const order_index = (maxOrderData?.order_index || 0) + 1

    const { data, error } = await supabase
      .from('project_phases')
      .insert({
        project_id: projectId,
        name: task.titre,
        description: task.description,
        status: task.status === 'done' ? 'completed' : task.status === 'in_progress' ? 'in_progress' : 'pending',
        order_index,
        end_date: task.due_date || task.end_date,
        start_date: task.start_date || null,
        notes: task.notes || null,
      })
      .select('*')
      .single()

    if (error) throw error

    // Create activity log
    await this.addActivity(
      projectId,
      'task_created',
      `Tâche créée: ${task.titre}`,
      undefined,
      { task_id: data.id }
    )

    return {
      id: data.id,
      created_at: data.created_at,
      updated_at: data.updated_at,
      project_id: data.project_id,
      titre: data.name,
      description: data.description,
      status: data.status === 'completed' ? 'done' : data.status === 'in_progress' ? 'in_progress' : 'todo',
      priority: 'medium' as const,
      due_date: data.end_date,
      assigned_to: null,
      ordre: data.order_index || 0,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      notes: data.notes || null,
      progress: data.progress || 0,
    } as ProjectTask
  },

  async updateTask(taskId: string, task: Partial<TaskFormData>): Promise<ProjectTask> {
    const updateData: Record<string, unknown> = {}
    if (task.titre !== undefined) updateData.name = task.titre
    if (task.description !== undefined) updateData.description = task.description
    if (task.status !== undefined) {
      updateData.status = task.status === 'done' ? 'completed' : task.status === 'in_progress' ? 'in_progress' : 'pending'
    }
    if (task.due_date !== undefined) updateData.end_date = task.due_date
    if (task.start_date !== undefined) updateData.start_date = task.start_date
    if (task.end_date !== undefined) updateData.end_date = task.end_date
    if (task.notes !== undefined) updateData.notes = task.notes
    if (task.progress !== undefined) updateData.progress = task.progress

    const { data, error } = await supabase
      .from('project_phases')
      .update(updateData)
      .eq('id', taskId)
      .select('*')
      .single()

    if (error) throw error

    return {
      id: data.id,
      created_at: data.created_at,
      updated_at: data.updated_at,
      project_id: data.project_id,
      titre: data.name,
      description: data.description,
      status: data.status === 'completed' ? 'done' : data.status === 'in_progress' ? 'in_progress' : 'todo',
      priority: 'medium' as const,
      due_date: data.end_date,
      assigned_to: null,
      ordre: data.order_index || 0,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      notes: data.notes || null,
      progress: data.progress || 0,
    } as ProjectTask
  },

  async deleteTask(taskId: string): Promise<void> {
    const { error } = await supabase.from('project_phases').delete().eq('id', taskId)
    if (error) throw error
  },

  async reorderTask(taskId: string, newOrdre: number): Promise<void> {
    const { error } = await supabase.rpc('reorder_task', {
      task_id_param: taskId,
      new_ordre_param: newOrdre,
    })
    if (error) throw error
  },

  // Create task reminder (1 day before due date)
  async createTaskReminder(
    taskId: string,
    projectId: string,
    taskTitle: string,
    dueDate: string,
    assignedTo: string
  ): Promise<void> {
    const { remindersService } = await import('./reminders')

    // Get admin email
    const { data: admin } = await supabase
      .from('admins')
      .select('email, nom, prenom')
      .eq('id', assignedTo)
      .single()

    if (!admin) return

    const fullName = `${admin.prenom || ''} ${admin.nom || ''}`.trim()

    // Calculate scheduled date (1 day before due date)
    const dueDateObj = new Date(dueDate)
    const scheduledDate = new Date(dueDateObj)
    scheduledDate.setDate(scheduledDate.getDate() - 1)

    // Create reminder
    await remindersService.createAutoReminder({
      type: 'task_due',
      reference_type: 'projet',
      reference_id: projectId,
      contact_id: assignedTo,
      contact_email: admin.email,
      contact_name: fullName,
      subject: `Rappel: Tâche "${taskTitle}" arrive à échéance`,
      message: `La tâche "${taskTitle}" doit être terminée pour le ${dueDateObj.toLocaleDateString('fr-FR')}`,
      priority: 'medium',
    })
  },

  // Get overdue tasks (using project_phases)
  async getOverdueTasks(projectId?: string): Promise<ProjectTask[]> {
    try {
      let query = supabase
        .from('project_phases')
        .select(`
          *,
          project:projects(id, title, user_id)
        `)
        .neq('status', 'completed')
        .lt('end_date', new Date().toISOString().split('T')[0])
        .order('end_date', { ascending: true })

      if (projectId) {
        query = query.eq('project_id', projectId)
      }

      const { data, error } = await query

      if (error) {
        console.warn('Error fetching overdue tasks:', error)
        return []
      }

      return (data || []).map(phase => ({
        id: phase.id,
        created_at: phase.created_at,
        updated_at: phase.updated_at,
        project_id: phase.project_id,
        titre: phase.name,
        description: phase.description,
        status: 'todo' as const,
        priority: 'medium' as const,
        due_date: phase.end_date,
        assigned_to: null,
        ordre: phase.order_index || 0,
        project: phase.project,
      })) as ProjectTask[]
    } catch {
      return []
    }
  },

  // Get active tasks (pending + in_progress) across all projects
  async getActiveTasks(): Promise<(ProjectTask & { project?: { id: string; title: string } })[]> {
    try {
      const { data, error } = await supabase
        .from('project_phases')
        .select(`
          *,
          project:projects(id, title)
        `)
        .in('status', ['pending', 'in_progress'])
        .order('updated_at', { ascending: false })
        .limit(20)

      if (error) {
        console.warn('Error fetching active tasks:', error)
        return []
      }

      return (data || []).map(phase => ({
        id: phase.id,
        created_at: phase.created_at,
        updated_at: phase.updated_at,
        project_id: phase.project_id,
        titre: phase.name,
        description: phase.description,
        status: phase.status === 'in_progress' ? 'in_progress' as const : 'todo' as const,
        priority: 'medium' as const,
        due_date: phase.end_date,
        assigned_to: null,
        ordre: phase.order_index || 0,
        start_date: phase.start_date || null,
        end_date: phase.end_date || null,
        notes: phase.notes || null,
        progress: phase.progress || 0,
        project: phase.project ? { id: phase.project.id, title: phase.project.title } : undefined,
      }))
    } catch {
      return []
    }
  },

  // Get tasks assigned to user - Note: project_phases doesn't support assignment
  async getMyTasks(_adminId: string): Promise<ProjectTask[]> {
    // project_phases ne supporte pas l'assignation à un utilisateur
    // Retourne un tableau vide pour le moment
    console.warn('getMyTasks: project_phases ne supporte pas l\'assignation')
    return []
  },

  // Documents - Fetch from both project_documents and documents tables
  async getDocuments(projectId: string): Promise<ProjectDocument[]> {
    // Fetch from project_documents table
    const { data: projectDocs, error: projectDocsError } = await supabase
      .from('project_documents')
      .select(`
        *,
        uploader:users(id, full_name, email, avatar_url)
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (projectDocsError) {
      console.warn('Error fetching project documents:', projectDocsError)
    }

    // Fetch from documents table (for auto-generated documents like signed quotes)
    const { data: generalDocs, error: generalDocsError } = await supabase
      .from('documents')
      .select(`
        *,
        uploader:users!user_id(id, full_name, email, avatar_url)
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (generalDocsError) {
      console.warn('Error fetching general documents:', generalDocsError)
    }

    // Convert general documents to ProjectDocument format
    const convertedGeneralDocs = (generalDocs || []).map((doc: Record<string, unknown>) => {
      return {
        id: doc.id,
        project_id: doc.project_id,
        nom: doc.name,
        description: null,
        type: doc.type,
        categorie: doc.category,
        url: doc.url,
        taille: doc.size,
        uploaded_by: doc.user_id,
        created_at: doc.created_at,
        updated_at: null,
        uploader: doc.uploader ? {
          id: (doc.uploader as Record<string, unknown>).id,
          full_name: (doc.uploader as Record<string, unknown>).full_name,
          email: (doc.uploader as Record<string, unknown>).email,
          avatar_url: (doc.uploader as Record<string, unknown>).avatar_url,
        } : null,
        _source: 'documents' // Flag to identify source table
      }
    })

    // Combine and sort by created_at
    const allDocs = [...(projectDocs || []), ...convertedGeneralDocs]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

    // Regenerate signed URLs (stored URL becomes stale after 1h on private buckets)
    const signed = await Promise.all(
      allDocs.map(async (doc: Record<string, unknown>) => {
        const sourceBucket = doc._source === 'documents' ? 'documents' : 'project-documents'
        const url = (doc.url as string | null) ?? null
        if (!url) return doc
        const marker = `/${sourceBucket}/`
        const afterMarker = url.split(marker).pop()
        const filePath = afterMarker?.split('?')[0]
        if (!filePath) return doc
        const { data: urlData, error: urlError } = await supabase.storage
          .from(sourceBucket)
          .createSignedUrl(filePath, 3600)
        if (urlError) {
          console.warn('Error signing project document URL:', urlError)
          return { ...doc, url: null }
        }
        return { ...doc, url: urlData?.signedUrl ?? null }
      })
    )

    return signed as ProjectDocument[]
  },

  async uploadDocument(
    projectId: string,
    file: File,
    categorie: DocumentCategory,
    description?: string,
    uploadedBy?: string
  ): Promise<ProjectDocument> {
    // Upload file to storage with extension validation
    const fileExt = (file.name.split('.').pop() || '').toLowerCase()
    const blockedExtensions = ['php', 'jsp', 'aspx', 'exe', 'sh', 'bat', 'cmd', 'ps1', 'cgi', 'pl']
    if (blockedExtensions.includes(fileExt)) {
      throw new Error(`File type .${fileExt} is not allowed`)
    }
    const bytes = new Uint8Array(8)
    crypto.getRandomValues(bytes)
    const randomPart = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    const fileName = `${projectId}/${Date.now()}_${randomPart}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('project-documents')
      .upload(fileName, file)

    if (uploadError) throw uploadError

    // Get signed URL (1h, regenerated on read via getDocuments)
    const { data: urlData, error: urlError } = await supabase.storage
      .from('project-documents')
      .createSignedUrl(fileName, 3600)
    if (urlError) {
      console.warn('Error signing project document URL:', urlError)
    }

    // Create document record
    const { data, error } = await supabase
      .from('project_documents')
      .insert({
        project_id: projectId,
        nom: file.name,
        description,
        type: file.type,
        categorie,
        url: urlData?.signedUrl ?? '',
        taille: file.size,
        uploaded_by: uploadedBy,
      })
      .select(`
        *,
        uploader:users(id, full_name, email, avatar_url)
      `)
      .single()

    if (error) throw error
    return data as ProjectDocument
  },

  async deleteDocument(documentId: string): Promise<void> {
    // Get document to find file path
    const { data: doc } = await supabase
      .from('project_documents')
      .select('url')
      .eq('id', documentId)
      .single()

    if (doc?.url) {
      // Extract file path from URL (public or signed) and delete from storage
      const rawPath = doc.url.split('/project-documents/').pop()
      const filePath = rawPath?.split('?')[0]
      if (filePath) {
        await supabase.storage.from('project-documents').remove([filePath])
      }
    }

    const { error } = await supabase.from('project_documents').delete().eq('id', documentId)
    if (error) throw error
  },

  async generateShareLink(documentId: string, expiresInHours = 24): Promise<string> {
    const bytes = new Uint8Array(24)
    crypto.getRandomValues(bytes)
    const token = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    const expiresAt = new Date(Date.now() + expiresInHours * 60 * 60 * 1000).toISOString()

    const { error } = await supabase
      .from('project_documents')
      .update({
        share_token: token,
        share_expires_at: expiresAt,
      })
      .eq('id', documentId)

    if (error) throw error

    return `${window.location.origin}/documents/share/${token}`
  },

  async getDocumentByShareToken(token: string): Promise<ProjectDocument | null> {
    const { data, error } = await supabase
      .from('project_documents')
      .select('*')
      .eq('share_token', token)
      .gt('share_expires_at', new Date().toISOString())
      .single()

    if (error) return null
    return data as ProjectDocument
  },

  // Milestones
  async getMilestones(projectId: string): Promise<ProjectMilestone[]> {
    const { data, error } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('project_id', projectId)
      .order('ordre', { ascending: true })

    if (error) throw error
    return data as ProjectMilestone[]
  },

  async createMilestone(projectId: string, milestone: MilestoneFormData): Promise<ProjectMilestone> {
    // Get the max order
    const { data: maxOrderData } = await supabase
      .from('project_milestones')
      .select('ordre')
      .eq('project_id', projectId)
      .order('ordre', { ascending: false })
      .limit(1)
      .single()

    const ordre = (maxOrderData?.ordre || 0) + 1

    const { data, error } = await supabase
      .from('project_milestones')
      .insert({
        ...milestone,
        project_id: projectId,
        status: 'pending',
        ordre,
      })
      .select()
      .single()

    if (error) throw error
    return data as ProjectMilestone
  },

  async updateMilestone(milestoneId: string, milestone: Partial<MilestoneFormData>): Promise<ProjectMilestone> {
    const { data, error } = await supabase
      .from('project_milestones')
      .update(milestone)
      .eq('id', milestoneId)
      .select()
      .single()

    if (error) throw error
    return data as ProjectMilestone
  },

  async completeMilestone(milestoneId: string): Promise<ProjectMilestone> {
    const { data, error } = await supabase
      .from('project_milestones')
      .update({
        status: 'completed',
        date_completee: new Date().toISOString(),
      })
      .eq('id', milestoneId)
      .select()
      .single()

    if (error) throw error
    return data as ProjectMilestone
  },

  async deleteMilestone(milestoneId: string): Promise<void> {
    const { error } = await supabase.from('project_milestones').delete().eq('id', milestoneId)
    if (error) throw error
  },

  // Activities
  async getActivities(projectId: string, limit = 50): Promise<Project['activities']> {
    try {
      const { data, error } = await supabase
        .from('project_activities')
        .select(`
          *,
          user:users!project_activities_user_id_fkey(id, full_name, email, avatar_url)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return (data as Project['activities']) || []
    } catch (e) {
      console.warn('project_activities table not available yet')
      return []
    }
  },

  async addActivity(
    projectId: string,
    type: Project['activities'][number]['type'],
    description: string,
    userId?: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    const { error } = await supabase
      .from('project_activities')
      .insert({
        project_id: projectId,
        type,
        description,
        user_id: userId,
        metadata,
      })

    if (error) throw error
  },

  // Stats
  async getStats(): Promise<{
    total: number
    byStatus: Record<string, number>
    overdue: number
    avgProgression: number
  }> {
    const { data, error } = await supabase
      .from('projects')
      .select('status, progression, date_fin')

    if (error) throw error

    const projects = data || []
    const now = new Date().toISOString()

    const byStatus: Record<string, number> = {}
    let totalProgression = 0
    let overdue = 0

    projects.forEach((p) => {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1
      totalProgression += p.progression || 0
      if (p.date_fin && p.date_fin < now && p.status !== 'completed' && p.status !== 'cancelled') {
        overdue++
      }
    })

    return {
      total: projects.length,
      byStatus,
      overdue,
      avgProgression: projects.length > 0 ? Math.round(totalProgression / projects.length) : 0,
    }
  },
}
